// Shared public marketing-content access for surfaces that need CMS copy but don't own a full
// retryable cms lifecycle (the sitewide footer, /contact, /solutions/*). ONE memoized fetch per
// session shared by every consumer — a page that also fetches /public/marketing itself (Landing,
// /pricing, /aira) keeps its own state and this cache simply de-duplicates the footer's read.
// A failed fetch resolves null (consumers keep their byte-mirrored fallback copy) and clears the
// memo so a later mount retries.
//
// CMS-3 (2026-08-24) — live revalidation. On a prerendered marketing route the FIRST call below
// resolves the deploy-time snapshot bootstrap (see consumeBootstrapPayload) and, historically,
// NOTHING ever checked again for the rest of the session: a platform-console CMS/pricing publish
// was invisible to every already-open tab and every fresh visit until the next deploy re-baked
// the snapshot (found reviewing why a pricing-model edit didn't show on /pricing in production).
// `revalidate()` below fixes this for every consumer of this module in one place: it re-fetches
// the live payload in the background right after the bootstrap is consumed, and again whenever a
// long-open tab regains focus or a same-interval timer elapses (throttled), broadcasting the
// fresh payload to subscribers ONLY when the published content actually changed — so an
// unchanged CMS never causes an extra re-render/reflow (the snapshot's CLS budget this bootstrap
// was built to protect stays intact).
//
// Conditional GET (RFC 7232), not a plain re-fetch: every revalidation sends the ETag it
// remembers as If-None-Match (backend/app/routers/public.py content_etag), so a periodic check
// that finds nothing new costs a bodyless 304 — a few hundred bytes — instead of re-transferring
// the ~30KB payload. Same "many rarely-changing readers" pattern GitHub's API uses for its own
// conditional-request support, and it's what makes polling on a timer (below) cheap enough to run
// on every open marketing tab rather than needing a push channel.
import { useEffect, useState } from "react";
import api from "./api";

// Don't re-check more than once per focus-throttle window — a visitor tabbing back and forth
// must never turn into a request storm; a CMS publish is a rare, deliberate operator action, not
// something that needs sub-minute freshness.
const REVALIDATE_FOCUS_MIN_INTERVAL_MS = 5 * 60 * 1000;

let inflight = null;
let currentContent = null;   // the latest known-good payload (bootstrap, fetch, or revalidation)
let revalidating = null;     // in-flight background revalidation, deduped across callers
let lastRevalidatedAt = 0;
let lastEtag = null;         // the server's ETag for currentContent — sent back as If-None-Match
const subscribers = new Set(); // live consumers to notify when a revalidation finds fresh content

// TECH-2 — prerender CMS bootstrap. scripts/prerender.mjs inlines the prerender-time
// /public/marketing payload into each CMS-fed snapshot as
//   <script id="vp360-cms-bootstrap">window.__VP360_MARKETING_BOOTSTRAP__={...}</script>
// so hydration renders the SAME published copy the snapshot was captured with (no flash of
// code-fallback text, no recoverable hydration mismatch). Consumed EXACTLY ONCE: the first
// getMarketingContent() call seeds the session memo from it and deletes the global, so a later
// cache reset (test seam) or SPA navigation falls back to the normal network fetch.
function consumeBootstrapPayload() {
  if (typeof window === "undefined") return null;
  const payload = window.__VP360_MARKETING_BOOTSTRAP__;
  try { delete window.__VP360_MARKETING_BOOTSTRAP__; } catch { /* non-configurable — ignore */ }
  return payload && typeof payload === "object" ? payload : null;
}

// Synchronous access to the snapshot's embedded payload, for seeding INITIAL React state.
// getMarketingContent() resolves through a Promise — a microtask AFTER the first hydrated
// render — so a page whose first render depends on the CMS briefly painted the code-fallback
// copy over the snapshot's published copy and the layout JUMPED when the promise landed
// (measured: the /pricing fixed-plans grid shifted ~28px; Lighthouse desktop CLS 0.105 against
// the 0.1 budget, the round-8 CI failure). Seeding useState from this makes the first hydrated
// render IDENTICAL to the snapshot; the shared promise memo is primed so every later consumer
// (SiteFooter etc.) stays coherent with the consume-once contract.
let bootstrapMemo = null;

export function getInitialMarketingContent() {
  if (!bootstrapMemo) {
    const payload = consumeBootstrapPayload();
    if (payload) {
      bootstrapMemo = payload;
      currentContent = payload;
      if (!inflight) inflight = Promise.resolve(payload);
      // CMS-3: this branch runs EXACTLY ONCE per session (guarded by the bootstrapMemo check
      // above) regardless of which consumer's lazy useState initializer reaches it first, so
      // triggering the background revalidation here — rather than from getMarketingContent()'s
      // `if (!inflight)` guard, which a second consumer's call would already skip — is the one
      // spot guaranteed to fire it exactly once, right after the bootstrap is actually consumed.
      revalidate();
    }
  }
  // Prefer the LATEST known-good content over the permanently-frozen bootstrap: once a
  // revalidation has landed a newer publish, a component that seeds its state from this
  // function on a LATER mount (an SPA nav away from and back to a CMS-fed route re-mounts
  // PricingPage/SiteFooter/etc. — not just the tab's first load) would otherwise flash the
  // original stale snapshot again for one render before self-correcting — a smaller-scale
  // recurrence of the exact bug this module exists to fix. On the very first call of a session
  // these are the same object, so this changes nothing about the initial-paint/CLS contract.
  return currentContent || bootstrapMemo;
}

function notifySubscribers(data) {
  // A subscriber's own error must never stop the rest from hearing about fresh content.
  subscribers.forEach((fn) => {
    try { fn(data); } catch { /* ignore */ }
  });
}

// Re-fetch the live payload and broadcast it to every subscriber iff it actually changed.
// Deduped: overlapping triggers (bootstrap consumption + a refocus in another mounted consumer)
// share the one in-flight request. Fails soft — a network/store error keeps serving the last
// known-good content and simply leaves the door open for the next trigger to try again.
//
// Conditional GET: sends the remembered ETag as If-None-Match; a 304 (nothing changed, no body)
// is treated exactly like "no data" below — the diff/broadcast step only ever runs on a real 200
// with a real body. `validateStatus` widens axios's default 2xx-only success range to include
// 304, so this is a clean status-code branch rather than relying on a 304 falling into the
// .catch() as if it were a network failure.
function revalidate() {
  if (revalidating) return revalidating;
  lastRevalidatedAt = Date.now();
  // Deliberately kicked off from inside a .then() rather than calling api.get() directly here:
  // this is a fire-and-forget background check, so even a synchronous throw from the client
  // (a misbehaving interceptor, an unconfigured test mock) must become a rejection the .catch
  // below can absorb, never a synchronous exception out of revalidate() into its caller.
  revalidating = Promise.resolve()
    .then(() => api.get("/public/marketing", {
      headers: lastEtag ? { "If-None-Match": lastEtag } : undefined,
      validateStatus: (status) => status === 200 || status === 304,
    }))
    .then((response) => {
      if (response.headers?.etag) lastEtag = response.headers.etag;
      return response.status === 304 ? null : response.data;
    })
    .catch(() => null)
    .then((data) => {
      revalidating = null;
      if (!data) return;
      if (JSON.stringify(data) !== JSON.stringify(currentContent)) {
        currentContent = data;
        inflight = Promise.resolve(data);
        notifySubscribers(data);
      }
    });
  return revalidating;
}

export function getMarketingContent() {
  if (!inflight) {
    const bootstrap = getInitialMarketingContent(); // also triggers the background revalidation
    if (bootstrap) {
      inflight = Promise.resolve(bootstrap);
      return inflight;
    }
    inflight = api
      .get("/public/marketing")
      .then((response) => {
        // A session that starts here (no bootstrap — dev mode, or a client-side SPA nav onto a
        // CMS-fed route from a non-CMS-fed one) still captures the ETag from its first live
        // fetch, so every LATER revalidate() call — periodic timer or tab refocus — can send
        // If-None-Match from the start instead of always paying for a full re-transfer.
        if (response.headers?.etag) lastEtag = response.headers.etag;
        return response.data;
      })
      .catch(() => {
        inflight = null;
        return null;
      });
    inflight.then((data) => { if (data) currentContent = data; });
  }
  return inflight;
}

// Subscribe to live CMS updates: `fn(data)` fires whenever a background revalidation finds the
// published payload changed. Also arms a throttled tab-refocus re-check so a long-open marketing
// tab picks up a publish without a manual reload, PLUS a same-interval timer for a tab that never
// blurs at all — the proactive check in getInitialMarketingContent() only fires on the bootstrap
// path (a prerendered snapshot's first consume), so a session that never got a bootstrap (dev
// mode; a client-side SPA nav onto a CMS-fed route from a non-CMS-fed one, e.g. from /login) had
// no trigger of its own until this subscriber's first visibility change — this closes that gap
// for the rare tab that's also never blurred. Multiple mounted consumers each arm their own timer
// here, but every check shares the same `lastRevalidatedAt` throttle guard, so they converge on
// one actual fetch per window regardless of how many are mounted. Returns an unsubscribe
// function; call it on unmount so an unmounted page doesn't keep the listener/timer alive.
export function subscribeMarketingContent(fn) {
  subscribers.add(fn);
  if (typeof document === "undefined") {
    return () => { subscribers.delete(fn); };
  }
  function checkAndRevalidate() {
    if (document.visibilityState !== "visible") return;
    if (Date.now() - lastRevalidatedAt < REVALIDATE_FOCUS_MIN_INTERVAL_MS) return;
    revalidate();
  }
  document.addEventListener("visibilitychange", checkAndRevalidate);
  const intervalId = (typeof window !== "undefined" ? window : globalThis)
    .setInterval(checkAndRevalidate, REVALIDATE_FOCUS_MIN_INTERVAL_MS);
  return () => {
    subscribers.delete(fn);
    document.removeEventListener("visibilitychange", checkAndRevalidate);
    clearInterval(intervalId);
  };
}

export function useMarketingContent() {
  const [cms, setCms] = useState(getInitialMarketingContent);
  useEffect(() => {
    let mounted = true;
    getMarketingContent().then((data) => {
      if (mounted && data) setCms(data);
    });
    const unsubscribe = subscribeMarketingContent((data) => {
      if (mounted) setCms(data);
    });
    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);
  return cms;
}

// test seam: reset the memo between jest cases
export function __resetMarketingContentCache() {
  bootstrapMemo = null;
  inflight = null;
  currentContent = null;
  revalidating = null;
  lastRevalidatedAt = 0;
  lastEtag = null;
  subscribers.clear();
}
